import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation, type TFunction } from 'react-i18next';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';

import type {
  IMapFinish,
  IMapFinishScalar,
  IMapFinishVector,
  IMapLayoutFinish,
} from '@interfaces/map';

import { useMapEditor } from '../MapEditorContext';
import { useMapEditorFinishRegistry } from '../useMapEditorFinishRegistry';
import {
  buildFinishEntry,
  clampScalar,
  describeFinishDiff,
  effectiveMaxFor,
  findFinishForSlot,
  finishOverrideKeys,
  formatScalar,
  hexToRgba,
  orderedFinishGroups,
  resolveFinishValues,
  rgbaToCss,
  rgbaToHex,
  sameFinishes,
  settleConstraints,
  upsertFinish,
} from '../mapEditorFinishes';
import { useFinishPreview } from '../useFinishPreview';
import { type MapLayoutFinishSet } from '../mapEditorUnreal';
import { useInspectorStyles } from './styles';

// Which segment's surfaces this section is about. Passed in rather than chosen inside it, and that is
// the redesign's rule for the whole right-hand panel: the panel is only ever about the segment the
// author selected in the walk, so a scope dropdown here would be a second, disagreeing answer to
// "which room am I working on".
//
// The layout-wide default is still reachable, through **Use this on every surface** below — it writes
// the same `setSlotFinish(null, ...)` the dropdown's "All rooms" option wrote. It is an action rather
// than a scope because that is what an author does with it: they dress one wall, see it, and then say
// "everywhere".
export interface IFinishesSectionProps {
  // Placement key of the segment. START_ROOM_KEY for the entrance, which carries its own finishes on
  // the wire like any other row.
  roomKey: string;
  // The semantic surfaces this segment's module offers, in display order. Empty is a quiet valid
  // state, not a warning: which surfaces a module can be re-dressed on is authored in its own level,
  // so a room that classifies no ceiling simply cannot be given one.
  offeredSlots: string[];
}

/**
 * Localizes a name the build shipped.
 *
 * The build ships English, and the hub owns translations for the vocabulary it knows — the schema
 * keys, the group ids, the semantic slot names. Anything it has never heard of falls through to the
 * shipped string, which is why a newer kit can add a parameter without this hub version hiding it.
 *
 * @param translate - i18next `t`.
 * @param bucket - Which vocabulary the name belongs to.
 * @param key - The stable handle: a schema key, a group id or a slot name.
 * @param fallback - The English the build shipped.
 * @returns The localized name, or the shipped one.
 */
const localizeFinishName = (
  translate: TFunction,
  bucket: 'finishParam' | 'finishGroup' | 'finishSlot',
  key: string,
  fallback: string,
): string => translate(`editorMode.${bucket}.${key}`, fallback);

/**
 * The one-line explanation of a parameter, if anyone has one.
 *
 * Two sources, in this order: a translation this hub ships for a key it knows, then the description
 * the kit authored. That order is what makes the panel both translated and open-ended — the
 * parameters shipped today read in the author's own language, and one added by a later kit still
 * explains itself in the kit's English instead of going silent.
 *
 * Falls back explicitly rather than trusting i18next's own default, which hands back the key itself
 * when the default value is empty — a tooltip reading `editorMode.finishParamHint.foo` is worse than
 * no tooltip.
 *
 * @param translate - i18next `t`.
 * @param key - Schema key of the parameter.
 * @param description - The kit's own description, when it shipped one.
 * @returns The hint, or empty when neither source has anything to say.
 */
const localizeFinishHint = (
  translate: TFunction,
  key: string,
  description = '',
): string => {
  const hintKey = `editorMode.finishParamHint.${key}`;
  const translated = translate(hintKey, description);

  return translated === hintKey ? description : translated;
};

/**
 * A control's label, with what the parameter does behind an icon beside it.
 *
 * The icon renders only where there is something to say — the kit may describe no parameter and this
 * hub may not know the key — so an undescribed parameter reads as a plain label rather than as an
 * empty tooltip. It sits against the end of the label text rather than out by the value, so the
 * question it answers ("what is this?") is next to the thing being asked about.
 *
 * @param props.paramKey - Schema key: the stable handle both the name and the hint are looked up by.
 * @param props.displayName - The English name the build shipped.
 * @param props.description - The kit's own explanation, used where this hub has no translation.
 */
const FinishControlLabel = ({
  paramKey,
  displayName,
  description,
}: {
  paramKey: string;
  displayName: string;
  description?: string;
}) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const hint = localizeFinishHint(translate, paramKey, description);

  return (
    <Box sx={styles.finishControlLabelGroup}>
      <Typography sx={styles.finishControlLabel}>
        {localizeFinishName(translate, 'finishParam', paramKey, displayName)}
      </Typography>
      {!!hint && (
        <Tooltip title={hint} placement="top" enterDelay={300}>
          <HelpOutlineOutlinedIcon
            sx={styles.finishControlHintIcon}
            data-testid={`finish-param-${paramKey}-hint`}
          />
        </Tooltip>
      )}
    </Box>
  );
};

/**
 * One choice in the finish grid: a preset, or no finish at all.
 *
 * Where the kit ships no thumbnail the tile names the choice and then the two or three parameters it
 * actually moves, which is the honest identifying detail and is already authored. A colour swatch was
 * the obvious alternative and does not work on this content: every shipped preset leaves `tint` at
 * the default, so a swatch grid is a row of identical white squares — which reads as a broken image
 * rather than as a choice.
 *
 * Acts on a click and nothing else. Picking is not destructive — it changes this author's draft and
 * publishes nothing — so the world already answers "what does this look like" as soon as a tile is
 * clicked, and a hover that did the same was a second mechanism for one job.
 *
 * @param props.title - Name of the choice.
 * @param props.subtitle - Shown under the title when there is no thumbnail.
 * @param props.thumbnailUrl - Artwork, when the kit ships any.
 * @param props.isSelected - Whether the surface currently carries this choice.
 * @param props.testId - Registered in tests/e2e/constants/test-ids.ts.
 * @param props.onSelect - Called when the author picks it.
 */
const FinishTile = ({
  title,
  subtitle,
  thumbnailUrl,
  isSelected,
  isDisabled = false,
  testId,
  onSelect,
}: {
  title: string;
  subtitle: string;
  thumbnailUrl?: string;
  isSelected: boolean;
  // Offered but not choosable, with the subtitle saying why. Only Original uses it, and only for the
  // one state the wire cannot express — see the Original tile below.
  isDisabled?: boolean;
  testId: string;
  onSelect: () => void;
}) => {
  const styles = useInspectorStyles();

  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      disabled={isDisabled}
      sx={[
        styles.finishTile,
        isSelected && styles.finishTileSelected,
        isDisabled && styles.finishTileDisabled,
      ]}
      data-testid={testId}
    >
      {thumbnailUrl ? (
        <Box
          component="img"
          src={thumbnailUrl}
          alt={title}
          sx={styles.finishTileImage}
        />
      ) : null}
      <Typography sx={styles.finishTileTitle}>{title}</Typography>
      {!thumbnailUrl && !!subtitle && (
        <Typography sx={styles.finishTileDiff}>{subtitle}</Typography>
      )}
    </Box>
  );
};

/**
 * The Finishes panel: which surface, which finish, and the parameters on top of it.
 *
 * Renders nothing at all on a kit that ships no finish schema, which is every build made before the
 * feature — the section is not an empty state there, it simply does not exist.
 */
export const FinishesSection = ({
  roomKey,
  offeredSlots,
}: IFinishesSectionProps) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const { finishes, setSlotFinish, clearSlotFinish } = useMapEditor();
  const registry = useMapEditorFinishRegistry();
  const { schedulePreview, commitPreview } = useFinishPreview();

  const [slot, setSlot] = useState<string>('');
  // Closed to begin with, which is the whole shape of this panel: a finish is chosen from a tile and
  // the numbers behind it are a second, rarer step. Seventeen sliders open by default push the tiles
  // and the surface switch off the top of a 360px panel, so the first thing an author sees is the
  // last thing they need.
  const [isTuningOpen, setIsTuningOpen] = useState(false);

  const schema = registry.schema;

  const slots = offeredSlots;

  // Picked rather than remembered when the current one is gone: the author moves between segments
  // under this panel and they do not offer the same surfaces, so a slot that this one lacks would
  // leave every control below disabled with nothing saying why.
  const activeSlot = slots.includes(slot) ? slot : slots[0] ?? '';

  // This segment's own overrides. Kept separate from what it *renders*, below, because the two are
  // different questions and only this one is what a write here replaces.
  const ownEntries = finishes.rooms[roomKey] ?? [];
  const own = activeSlot
    ? findFinishForSlot(ownEntries, activeSlot)
    : undefined;

  // What the surface actually renders: this segment's override, or the layout's default where it has
  // none. The tiles show this, so the selected tile is the finish the author is looking at in the
  // world rather than "Original" for every segment they have not dressed individually.
  const inherited = activeSlot
    ? findFinishForSlot(finishes.defaults, activeSlot)
    : undefined;
  const applied = own ?? inherited;

  // The surface takes the layout default and nothing of its own. Worth saying on screen, because the
  // controls below look identical either way and one of them cannot act: "Original" writes the absence
  // of an override, and a surface that has no override already is not changed by writing one.
  //
  // The wire has no third state to write — an unfinished surface is expressed by having no entry, so
  // there is no way to say "this segment, specifically, undressed" while a default stands. Naming the
  // situation is the honest answer; the control that changes it is the every-surface button.
  const isInherited = !own && !!inherited;

  const preset = useMemo(
    () =>
      registry.finishes.find((candidate) => candidate.id === applied?.finish) ??
      null,
    [registry.finishes, applied?.finish],
  );

  const overridden = useMemo(
    () =>
      schema && applied
        ? finishOverrideKeys(schema, applied, preset)
        : new Set<string>(),
    [schema, applied, preset],
  );

  const groups = useMemo(() => orderedFinishGroups(schema), [schema]);

  /**
   * The finish set as it would be with one entry replaced, without touching the draft.
   *
   * What the hover preview is built on: the author is being shown a finish they have not chosen, so
   * nothing may be written down — leaving the tile has to put the world back exactly as it was.
   */
  const withEntry = useCallback(
    (
      entry: IMapLayoutFinish | null,
      // Which level the entry goes on: this segment by default, or the layout's own default when the
      // caller passes null. Parameterised rather than fixed to the segment so the every-surface action
      // previews through the same path — a second copy of this would preview one level and write the
      // other.
      scope: string | null = roomKey,
    ): MapLayoutFinishSet => {
      if (!entry) {
        return finishes;
      }

      if (!scope) {
        return {
          ...finishes,
          defaults: upsertFinish(finishes.defaults, entry),
        };
      }

      return {
        ...finishes,
        rooms: {
          ...finishes.rooms,
          [scope]: upsertFinish(finishes.rooms[scope] ?? [], entry),
        },
      };
    },
    [finishes, roomKey],
  );

  const handlePickFinish = useCallback(
    (finish: IMapFinish) => {
      if (!schema || !activeSlot) {
        return;
      }

      const entry = buildFinishEntry(schema, activeSlot, finish);
      setSlotFinish(roomKey, entry);
      commitPreview(withEntry(entry));
    },
    [schema, activeSlot, roomKey, setSlotFinish, commitPreview, withEntry],
  );

  /**
   * The finish set with this surface undressed, without touching the draft.
   *
   * Built by hand rather than read back from the context for the same reason withEntry is: the state
   * update has not landed in the render that sends the preview, so reading it back would describe the
   * draft as it was rather than as it will be.
   */
  const withoutEntry = useCallback(
    (scope: string | null = roomKey): MapLayoutFinishSet => {
      if (!activeSlot) {
        return finishes;
      }

      if (!scope) {
        return {
          ...finishes,
          defaults: finishes.defaults.filter(
            (entry) => entry.slot !== activeSlot,
          ),
        };
      }

      return {
        ...finishes,
        rooms: {
          ...finishes.rooms,
          [scope]: (finishes.rooms[scope] ?? []).filter(
            (entry) => entry.slot !== activeSlot,
          ),
        },
      };
    },
    [activeSlot, roomKey, finishes],
  );

  const handleClear = useCallback(() => {
    if (!activeSlot) {
      return;
    }
    clearSlotFinish(roomKey, activeSlot);
    commitPreview(withoutEntry());
  }, [activeSlot, roomKey, clearSlotFinish, commitPreview, withoutEntry]);

  /**
   * Puts what this surface is wearing on the same surface of every segment that offers it.
   *
   * Written as the layout's own default rather than as an override on each segment, which is the one
   * decision in this control worth stating. Both would look identical in the world; only one of them
   * fits the wire. A twenty-segment layout with seventeen resolved parameters per entry is what the
   * publish budget refuses, and a default is one entry however long the walk gets.
   *
   * The segment's own override goes with it. Left standing it would win over the default it was just
   * used to set, so the one segment the author was looking at would be the one that stopped tracking
   * later changes to it — and nothing on screen would say why.
   */
  const handleApplyEverywhere = useCallback(() => {
    if (!activeSlot || !applied) {
      return;
    }

    const entry: IMapLayoutFinish = { ...applied, slot: activeSlot };
    setSlotFinish(null, entry);
    clearSlotFinish(roomKey, activeSlot);

    // Previewed as both writes together. Sending either on its own describes a draft that never
    // exists — the override still winning, or nothing dressed at all.
    const next = withEntry(entry, null);
    commitPreview({
      ...next,
      rooms: {
        ...next.rooms,
        [roomKey]: (next.rooms[roomKey] ?? []).filter(
          (existing) => existing.slot !== activeSlot,
        ),
      },
    });
  }, [
    activeSlot,
    applied,
    roomKey,
    setSlotFinish,
    clearSlotFinish,
    withEntry,
    commitPreview,
  ]);

  /**
   * Writes one scalar and previews it.
   *
   * `live` distinguishes a drag in progress from its release: mid-drag the send is throttled and
   * coalesced, and on release it goes immediately, so the world always settles on the value the
   * slider is actually resting at.
   */
  const handleScalarChange = useCallback(
    (scalar: IMapFinishScalar, value: number, live: boolean) => {
      if (!schema || !applied) {
        return;
      }

      const scalars = settleConstraints(schema, {
        ...applied.scalars,
        [scalar.key]: clampScalar(scalar, value),
      });
      const entry: IMapLayoutFinish = { ...applied, scalars };

      setSlotFinish(roomKey, entry);
      const next = withEntry(entry);
      if (live) {
        schedulePreview(next);
        return;
      }
      commitPreview(next);
    },
    [
      schema,
      applied,
      roomKey,
      setSlotFinish,
      withEntry,
      schedulePreview,
      commitPreview,
    ],
  );

  const handleVectorChange = useCallback(
    (vector: IMapFinishVector, hex: string) => {
      if (!applied) {
        return;
      }

      const entry: IMapLayoutFinish = {
        ...applied,
        vectors: {
          ...applied.vectors,
          [vector.key]: hexToRgba(hex, applied.vectors[vector.key]?.[3] ?? 1),
        },
      };

      setSlotFinish(roomKey, entry);
      commitPreview(withEntry(entry));
    },
    [applied, roomKey, setSlotFinish, commitPreview, withEntry],
  );

  // The entry as the preset defines it, which is what "reset" means and also what tells us whether
  // there is anything to reset. Rebuilt rather than remembered: the preset is the authority, and a
  // remembered copy would go stale the moment the catalog shipped a new build.
  const pristine = useMemo(
    () =>
      schema && preset && activeSlot
        ? buildFinishEntry(schema, activeSlot, preset)
        : null,
    [schema, preset, activeSlot],
  );

  const isPristine =
    !!applied && !!pristine && sameFinishes([applied], [pristine]);

  /**
   * Puts the whole finish back to the preset it came from.
   *
   * Covers what the per-group resets cannot: the variant and the texture set travel with the entry
   * rather than with a group, so a group reset leaves them wherever they were. Reaching the same
   * state by pressing four group resets was also just tedious.
   */
  const handleResetAll = useCallback(() => {
    if (!pristine) {
      return;
    }

    setSlotFinish(roomKey, pristine);
    commitPreview(withEntry(pristine));
  }, [pristine, roomKey, setSlotFinish, commitPreview, withEntry]);

  const handleResetGroup = useCallback(
    (scalars: IMapFinishScalar[], vectors: IMapFinishVector[]) => {
      if (!schema || !applied) {
        return;
      }

      const base = resolveFinishValues(schema, preset);
      const entry: IMapLayoutFinish = {
        ...applied,
        scalars: settleConstraints(
          schema,
          scalars.reduce(
            (carried, scalar) => ({
              ...carried,
              [scalar.key]: base.scalars[scalar.key],
            }),
            applied.scalars,
          ),
        ),
        vectors: vectors.reduce(
          (carried, vector) => ({
            ...carried,
            [vector.key]: base.vectors[vector.key],
          }),
          applied.vectors,
        ),
      };

      setSlotFinish(roomKey, entry);
      commitPreview(withEntry(entry));
    },
    [schema, applied, preset, roomKey, setSlotFinish, commitPreview, withEntry],
  );

  // A build that ships no finish registry has no finish menu. Not an empty state — there is nothing
  // this map can be asked about.
  if (!schema) {
    return null;
  }

  return (
    <Box sx={styles.finishSection} data-testid="finishes-section">
      <Typography sx={styles.finishSectionTitle}>
        {translate('editorMode.finishesTitle', 'Finishes')}
      </Typography>

      {!slots.length ? (
        <Typography sx={styles.emptyHint}>
          {/* A quiet valid state rather than a warning. Which surfaces a module offers is authored in
              its own level, so a segment that classifies none is finished art rather than a gap — and
              the sentence says what would change it. */}
          {translate(
            'editorMode.finishesNoSurfacesHere',
            "This segment names no surfaces that can be re-dressed. They are classified in the module's own level.",
          )}
        </Typography>
      ) : (
        <>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={activeSlot}
            onChange={(_event, next) => next && setSlot(next)}
            sx={styles.finishSlotGroup}
          >
            {slots.map((name) => (
              <ToggleButton
                key={name}
                value={name}
                sx={styles.finishSlotButton}
                data-testid={`finish-slot-${name}-btn`}
              >
                {localizeFinishName(translate, 'finishSlot', name, name)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {/* Says where the finish on this surface came from. The controls look identical whether the
              segment carries its own or is taking the layout's, and the consequence does not: one is
              this segment's to change and the other is every segment's. */}
          <Typography sx={styles.finishScopeHint}>
            {isInherited
              ? translate(
                  'editorMode.finishesFromDefault',
                  'From the layout default — every segment with this surface is wearing it.',
                )
              : translate(
                  'editorMode.finishesThisSegment',
                  'This segment only — it overrides the layout default.',
                )}
          </Typography>

          <Box sx={styles.finishTileGrid}>
            {/* First, and a tile rather than a button below the grid: "as the kit shipped it" is one
                of the choices being made here, not a separate act of undoing. It also makes the
                un-finished state visible — a grid with nothing selected says nothing — and clicking
                it shows the original surface, which is the only way to see it while the kit ships no
                thumbnails. */}
            {/* Unavailable while the surface is wearing the layout default, because the wire has no
                way to express it: an unfinished surface is expressed by *having no entry*, so writing
                the absence of an override for a segment that already has none changes nothing. A
                clickable tile that did nothing would be worse than one that says why it cannot. The
                every-surface control below is what changes the default. */}
            <FinishTile
              title={translate('editorMode.finishesOriginalTile', 'Original')}
              subtitle={
                isInherited
                  ? translate(
                      'editorMode.finishesOriginalUnavailable',
                      'Change the layout default to undress this',
                    )
                  : translate(
                      'editorMode.finishesOriginalTileHint',
                      'The surface as the kit built it',
                    )
              }
              isSelected={!applied}
              isDisabled={isInherited}
              testId="finish-preset-none-tile"
              onSelect={handleClear}
            />
            {registry.finishes.map((finish) => (
              <FinishTile
                key={finish.id}
                title={finish.displayName}
                subtitle={describeFinishDiff(schema, finish).join(' · ')}
                thumbnailUrl={finish.thumbnailUrl}
                isSelected={applied?.finish === finish.id}
                testId={`finish-preset-${finish.id}-tile`}
                onSelect={() => handlePickFinish(finish)}
              />
            ))}
          </Box>

          {!applied ? null : (
            <>
              <Box sx={styles.finishRow}>
                <Typography sx={styles.finishFieldLabel}>
                  {translate('editorMode.finishesTextureSet', 'Texture set')}
                </Typography>
                <Typography sx={styles.finishFieldValue}>
                  {registry.textureSets.find(
                    (set) => set.id === applied.textureSet,
                  )?.displayName ?? applied.textureSet}
                </Typography>
              </Box>

              {/* The two whole-finish actions, on their own row and apart from the texture set they
                  used to sit beside — Clear next to "Texture set" read as "clear the texture", and
                  it removes the finish outright. */}
              <Box sx={styles.finishActionsRow}>
                <Tooltip
                  title={translate(
                    'editorMode.finishesResetAllHint',
                    'Put every value, and the texture set, back to what this finish came with',
                  )}
                  placement="top"
                  enterDelay={300}
                >
                  <Box component="span">
                    <Button
                      size="small"
                      sx={styles.finishResetAllButton}
                      onClick={handleResetAll}
                      disabled={!pristine || isPristine}
                      startIcon={
                        <RestartAltOutlinedIcon sx={{ fontSize: 14 }} />
                      }
                      data-testid="finish-reset-all-btn"
                    >
                      {translate(
                        'editorMode.finishesResetAll',
                        'Reset to finish',
                      )}
                    </Button>
                  </Box>
                </Tooltip>
              </Box>

              <Box sx={styles.finishTuningHeader}>
                <Typography sx={styles.finishGroupTitle}>
                  {translate('editorMode.finishesTuning', 'Fine-tuning')}
                </Typography>
                {/* Says what is hidden while it is hidden. Without it, collapsing the groups also
                    conceals that the finish has been moved away from its preset, and the panel would
                    be quietly lying about which surface the author is looking at. */}
                {!isTuningOpen && overridden.size > 0 && (
                  <Typography
                    sx={styles.finishTuningChanged}
                    data-testid="finish-tuning-changed"
                  >
                    {translate(
                      'editorMode.finishesTuningChanged',
                      '{{count}} changed',
                      { count: overridden.size },
                    )}
                  </Typography>
                )}
                <Tooltip
                  title={translate(
                    'editorMode.finishesTuningHint',
                    'The numbers behind this finish — tiling, relief, surface. The finish sets them; open this to adjust them by hand.',
                  )}
                  placement="left"
                  enterDelay={300}
                >
                  <IconButton
                    size="small"
                    sx={styles.finishTuningToggle}
                    onClick={() => setIsTuningOpen((open) => !open)}
                    aria-expanded={isTuningOpen}
                    aria-label={translate(
                      'editorMode.finishesTuning',
                      'Fine-tuning',
                    )}
                    data-testid="finish-tuning-toggle-btn"
                  >
                    {isTuningOpen ? (
                      <ExpandLessOutlinedIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <ExpandMoreOutlinedIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>

              {isTuningOpen &&
                groups.map((view) => (
                  <Box
                    key={view.group.id || 'other'}
                    sx={styles.finishGroup}
                    data-testid={`finish-group-${view.group.id || 'other'}`}
                  >
                    <Box sx={styles.finishGroupHeader}>
                      <Typography sx={styles.finishGroupTitle}>
                        {localizeFinishName(
                          translate,
                          'finishGroup',
                          view.group.id,
                          view.group.displayName ||
                            translate('editorMode.finishGroupOther', 'Other'),
                        )}
                      </Typography>
                      <Tooltip
                        title={translate(
                          'editorMode.finishesResetGroupHint',
                          'Put this group back to the values the finish came with',
                        )}
                        placement="left"
                        enterDelay={300}
                      >
                        <Button
                          size="small"
                          sx={styles.finishResetButton}
                          onClick={() =>
                            handleResetGroup(view.scalars, view.vectors)
                          }
                          startIcon={
                            <RestartAltOutlinedIcon sx={{ fontSize: 14 }} />
                          }
                          data-testid={`finish-group-${
                            view.group.id || 'other'
                          }-reset-btn`}
                        >
                          {translate('editorMode.finishesReset', 'Reset')}
                        </Button>
                      </Tooltip>
                    </Box>

                    {view.scalars.map((scalar) => {
                      const value =
                        applied.scalars[scalar.key] ?? scalar.default;
                      const max = effectiveMaxFor(
                        scalar,
                        applied.scalars,
                        schema,
                      );

                      return (
                        <Box key={scalar.key} sx={styles.finishControl}>
                          <Box sx={styles.finishControlHeader}>
                            <FinishControlLabel
                              paramKey={scalar.key}
                              displayName={scalar.displayName}
                              description={scalar.description}
                            />
                            {scalar.perfCritical && (
                              <Tooltip
                                title={translate(
                                  'editorMode.finishesPerfHint',
                                  'Costs frame time as it rises — raise it only as far as the surface needs.',
                                )}
                                placement="top"
                                enterDelay={300}
                              >
                                <BoltOutlinedIcon
                                  sx={{ fontSize: 13, color: '#FFB800' }}
                                  data-testid={`finish-param-${scalar.key}-perf`}
                                />
                              </Tooltip>
                            )}
                            {overridden.has(scalar.key) && (
                              <Box
                                sx={styles.finishModifiedDot}
                                data-testid={`finish-param-${scalar.key}-modified`}
                              />
                            )}
                            <Typography sx={styles.finishControlValue}>
                              {formatScalar(scalar, value)}
                            </Typography>
                          </Box>
                          <Slider
                            size="small"
                            min={scalar.min}
                            max={max}
                            step={scalar.step}
                            value={Math.min(value, max)}
                            onChange={(_event, next) =>
                              handleScalarChange(
                                scalar,
                                Array.isArray(next) ? next[0] : next,
                                /*live=*/ true,
                              )
                            }
                            onChangeCommitted={(_event, next) =>
                              handleScalarChange(
                                scalar,
                                Array.isArray(next) ? next[0] : next,
                                /*live=*/ false,
                              )
                            }
                            sx={styles.finishSlider}
                            slotProps={
                              {
                                input: {
                                  'data-testid': `finish-param-${scalar.key}-slider`,
                                },
                              } as never
                            }
                          />
                          {/* Said out loud rather than left to a slider that simply stops early: the
                            ceiling belongs to another control, so an author who does not know that
                            reads a stuck slider as a bug. */}
                          {scalar.mustNotExceed && max < scalar.max && (
                            <Typography sx={styles.finishConstraintHint}>
                              {translate(
                                'editorMode.finishesConstraintHint',
                                'Capped by {{other}} ({{max}})',
                                {
                                  other: localizeFinishName(
                                    translate,
                                    'finishParam',
                                    scalar.mustNotExceed,
                                    scalar.mustNotExceed,
                                  ),
                                  max: formatScalar(scalar, max),
                                },
                              )}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}

                    {view.vectors.map((vector) => {
                      const value =
                        applied.vectors[vector.key] ?? vector.default;

                      return (
                        <Box key={vector.key} sx={styles.finishControl}>
                          <Box sx={styles.finishControlHeader}>
                            <FinishControlLabel
                              paramKey={vector.key}
                              displayName={vector.displayName}
                              description={vector.description}
                            />
                            {overridden.has(vector.key) && (
                              <Box
                                sx={styles.finishModifiedDot}
                                data-testid={`finish-param-${vector.key}-modified`}
                              />
                            )}
                            <Box
                              sx={[
                                styles.finishSwatch,
                                { backgroundColor: rgbaToCss(value) },
                              ]}
                            />
                          </Box>
                          <Box
                            component="input"
                            type="color"
                            value={rgbaToHex(value)}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => handleVectorChange(vector, event.target.value)}
                            sx={styles.finishColorInput}
                            data-testid={`finish-param-${vector.key}-color`}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                ))}
            </>
          )}

          {/* Last, because it is the step after the choice rather than part of making it: an author
              dresses one surface, looks at it, and then says "everywhere". Offered only once there is
              something to spread — and not while this surface is already wearing the default, where it
              would write back what it just read. */}
          <Button
            sx={styles.finishApplyEverywhere}
            onClick={handleApplyEverywhere}
            disabled={!applied || isInherited}
            data-testid="finish-apply-everywhere"
          >
            {translate(
              'editorMode.finishesApplyEverywhere',
              'Use this on every {{surface}}',
              {
                surface: localizeFinishName(
                  translate,
                  'finishSlot',
                  activeSlot,
                  activeSlot,
                ).toLowerCase(),
              },
            )}
          </Button>
        </>
      )}
    </Box>
  );
};

export default FinishesSection;
